import {
  Children,
  ReactChild,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import {getEnv} from '../../../../common/env/env';
import {ServerEnv} from '../../../../common/env/ServerEnv.enum';
import {requestContext} from '../../../../common/requestContext';
import {IS_CLIENT_SIDE} from '../../../../common/shared/utils/constants';
import {getValidChildren} from '../../../../common/shared/utils/getValidChildren';
import {isTrue} from '../../../../common/shared/utils/isTrue';
import {twMerge} from '../../../../common/twMerge';
import {useIsResponsiveFoldable} from '../../../../common/useIsResponsiveFoldable.hook';
import useTypedSelector from '../../../../common/useTypedSelector';
import {BigBanner} from '../../../../design-system/organisms/BigBanner/BigBanner';
import {
  BannerItem,
  BannerViewType,
  GroupType,
} from '../../../../graphql/generated/types';
import {putTiaraObjectProps} from '../../../../logging/putTiaraObjectProps';
import {getBannerStartAutoplayEventKey} from '../../../../main/getBannerStartAutoplayEventKey.function';
import {getBannerStopAutoplayEventKey} from '../../../../main/getBannerStopAutoplayEventKey.function';
import {
  setBannerHasIndicator,
  setSelectedBanner,
} from '../../../../pagewebLayout/PagewebLayoutSlice';
import {CommonGroupProps} from '../../type';

export function Banner({children, group}: CommonGroupProps) {
  const validChildren = useMemo(
    () => getValidChildren(children) as ReactChild[],
    [children]
  );
  const {items, firstItem} = useTypedSelector(state => {
    const itemsInGroup =
      state.pagewebLayout.relations.groups[group.id].childrenIds;
    const firstItem = itemsInGroup?.[0]
      ? (state.pagewebLayout.entities.items[itemsInGroup[0]] as BannerItem)
      : undefined;
    return {
      items: itemsInGroup,
      firstItem,
    };
  });
  const dispatch = useDispatch();
  const onChangeIndex = useCallback(
    (index: number) => {
      if (!items || !items[index]) {
        return;
      }
      dispatch(
        setSelectedBanner({
          groupId: group.id,
          selectedItemId: items[index],
        })
      );
    },
    [group, items, dispatch]
  );

  const [isBannerAutoplay, setIsBannerAutoplay] = useState(
    getEnv().envName === ServerEnv.Real
      ? true
      : IS_CLIENT_SIDE &&
          isTrue(window?.localStorage?.getItem('STOP_BANNER_AUTOPLAY_FOR_TEST'))
        ? false
        : true
  );

  useEffect(() => {
    function startAutoplay() {
      setIsBannerAutoplay(true);
    }
    function stopAutoplay() {
      setIsBannerAutoplay(false);
    }
    window.addEventListener(
      getBannerStartAutoplayEventKey(group.id),
      startAutoplay
    );
    window.addEventListener(
      getBannerStopAutoplayEventKey(group.id),
      stopAutoplay
    );
    return () => {
      window.removeEventListener(
        getBannerStartAutoplayEventKey(group.id),
        startAutoplay
      );
      window.removeEventListener(
        getBannerStopAutoplayEventKey(group.id),
        stopAutoplay
      );
    };
  }, [group.id]);

  const {isMobile} = requestContext.requestInfo.display;
  const hasSingleItem = firstItem && Children.count(validChildren) === 1;

  const bannerViewType = firstItem?.bannerViewType;

  const hasIndicator = useMemo(
    () => Children.count(validChildren) > 1,
    [validChildren]
  );

  useEffect(() => {
    dispatch(
      setBannerHasIndicator({
        groupId: group.id,
        hasIndicator,
      })
    );
    return () => {
      dispatch(
        setBannerHasIndicator({
          groupId: group.id,
          hasIndicator: false,
        })
      );
    };
  }, [dispatch, group.id, hasIndicator]);

  const isTopBanner = group.type === GroupType.BigBanner;
  const isFoldable = useIsResponsiveFoldable();

  return (
    <BigBanner
      bgImage={firstItem?.thumbnail ?? ''}
      onChangeIndex={onChangeIndex}
      hasSingleItem={hasSingleItem}
      hasArrow={Children.count(validChildren) > 1}
      hasIndicator={hasIndicator}
      isAutoPlay={isBannerAutoplay}
      isTopBanner={isTopBanner}
      className={twMerge(
        // 높이를 잡아주지 않으면 breakpoint를 계산하는 동안 이미지가 확대 -> 축소되는 문제가 있어 빅배너 정책에 맞는 고정높이를 잡아줍니다.
        // 모바일&폴더블은 총너비 기준으로 계산하는 vw를 사용하고, PC는 956px의 고정 너비를 기반으로 계산했습니다.
        firstItem &&
          isTopBanner &&
          (bannerViewType === BannerViewType.Rectangle
            ? [
                isMobile && 'foldable:min-h-[33.7vw]',
                !isMobile && 'min-h-[258.75px]',
              ]
            : [
                isMobile && 'foldable:min-h-[58vw]',
                !isMobile && 'min-h-[460px]',
              ]),
        // 원래 Groups에서 공통으로 좌우 11px 패딩을 잡아줘야하는데, 모바일에서는 빅배너 내부에 gap을 잡아줘야해서 따로 처리합니다.
        !isMobile && 'mx-11pxr'
      )}
      isMobile={isMobile}
      isFoldable={isFoldable}
      {...putTiaraObjectProps({
        click: {
          layer2: '빅배너',
        },
        viewimp_contents: {
          layer2: '빅배너',
        },
      })}
    >
      {validChildren}
    </BigBanner>
  );
}
